import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getData, OAuthError, postData } from "../../backend";
import { useModal } from "../../contexts";
import useUser from "../../hooks/useUser";
import ErrorModal from "../ErrorModal";
import OAuthModal from "../OAuthModal";
import OAuthModalMicrosoft from "../OAuthModalMicrosoft";

interface Props {
  onReadyChange: (ready: boolean) => void;
}

function OnboardingDriveConnect({ onReadyChange }: Props) {
  const { t } = useTranslation();
  const { user } = useUser();
  const { openModal } = useModal();

  const isMicrosoft = user?.authProvider === "MICROSOFT";
  const email = isMicrosoft
    ? user?.microsoftCredentials?.microsoftEmailAddress || user?.email
    : user?.email;

  const [driveOk, setDriveOk] = useState(false);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    setChecking(true);
    try {
      if (isMicrosoft) {
        await postData("/microsoft/access-token/");
        setDriveOk(true);
      } else {
        const { scopes = [] } = await getData("/auth/scopes/");
        setDriveOk(scopes.some((s: string) => s.includes("/auth/drive")));
      }
    } catch {
      setDriveOk(false);
    } finally {
      setChecking(false);
    }
  }, [isMicrosoft]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);
  useEffect(() => { onReadyChange(driveOk); }, [driveOk, onReadyChange]);

  async function authorize(reconnect: boolean) {
    try {
      if (isMicrosoft) {
        const { url } = await postData("/microsoft/install/");
        openModal(<OAuthModalMicrosoft oauthUrl={url} onClose={refresh} />);
        return;
      }
      if (reconnect) {
        window.open("https://myaccount.google.com/permissions", "_blank");
        return;
      }
      await postData("/auth/access-token/", { scopes: ["https://www.googleapis.com/auth/drive", "https://www.googleapis.com/auth/gmail.send"] });
      await refresh();
    } catch (error) {
      if (error instanceof OAuthError)
        openModal(isMicrosoft
          ? <OAuthModalMicrosoft oauthUrl={error.oauthUrl} onClose={refresh} />
          : <OAuthModal oauthUrl={error.oauthUrl} onClose={refresh} />);
      else
        openModal(<ErrorModal details={error} />);
    }
  }

  const driveName = isMicrosoft ? t("OneDrive") : t("Google Drive");
  const driveIcon = `${process.env.REACT_APP_STATIC_URL}/${isMicrosoft ? "onedrive-icon.svg" : "drive-icon.svg"}`;

  return (
    <div className="flex flex-col gap-3 w-full mt-6">
      <p className="font-gilroy font-semibold text-sm text-gray-400">
        {isMicrosoft
          ? t("Please connect your OneDrive so Portant can generate and deliver your documents.")
          : t("Please connect your Google Drive so Portant can generate and deliver your documents.")}
      </p>
      <div className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={driveIcon} alt="" className="h-8 w-8 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-gilroy font-semibold text-gray-600 flex items-center gap-2">
              {driveName}
              {driveOk && <span className="text-green text-sm">{t("Connected")}</span>}
            </div>
            <div className="font-gilroy font-semibold text-gray-400 text-xs truncate">
              {checking ? t("Checking...") : driveOk ? email : t("Not connected")}
            </div>
          </div>
        </div>
        <button
          type="button"
          className={`btn text-sm py-1.5 px-4 flex-shrink-0 ${driveOk ? "btn-white" : "btn-blue"}`}
          onClick={() => authorize(driveOk)}
          disabled={checking}
        >
          {driveOk ? t("Reconnect") : t("Connect")}
        </button>
      </div>
    </div>
  );
}

export default OnboardingDriveConnect;
